// src/Prescription.js
import React from "react";
import { Doctor, Hospital } from "../lib/utils/types";
import { BookingData } from "../services/types";
import moment from "moment-timezone";
import { getCookie } from "../lib/utils/cookies";

// ✅ Sanitize function to remove emojis
const sanitizeInput = (value: string): string => {
  if (!value) return value;
  return value.replace(
    /([\u2700-\u27BF]|[\uE000-\uF8FF]|[\uD83C-\uDBFF\uDC00-\uDFFF]|\u24C2|\uFE0F|\u200D)+/g,
    ""
  );
};

interface Medicine {
  name: string;
  dosage: string;
  duration: string;
}
interface Vitals {
  bp?: string;
  weight?: string;
  pulse?: string;
  temperature?: string;
}
interface ExaminationFinding {
  finding: string;
  result: string;
}

interface Props {
  hospData: Hospital | undefined;
  docDetails: Doctor | undefined;
  booking: BookingData | undefined;
  medicines: Medicine[];
  complaints: string;
  historyAndExaminations: string;
  diagnosis: string;
  investigations: string;
  previousMedicines: string;
  advice: string;
  signatureImageUrl?: string;
  age?: string;
  gender?: string;
  type?: string;
  patientName?: string;
  patientContactNumber?: string | undefined;
  vitals?: Vitals;
  examinations?: ExaminationFinding[];
}

const PrescriptionPdf: React.FC<Props> = ({
  hospData,
  docDetails,
  booking,
  medicines,
  complaints,
  historyAndExaminations,
  diagnosis,
  investigations,
  previousMedicines,
  advice,
  signatureImageUrl,
  type,
  age,
  gender,
  patientName,
  patientContactNumber,
  vitals,
  examinations,
}) => {
  const timezone = getCookie("timezone");

  const renderFormattedText = (value: string) => {
    if (!value) return null;

    const sanitized = sanitizeInput(value);
    const boldSplit = sanitized.split(/(\*[^*]+\*)/g);

    return boldSplit.map((seg, i) => {
      const isBold = seg.startsWith("*") && seg.endsWith("*");
      const text = isBold ? seg.replace(/^\*(.*)\*$/, "$1") : seg;
      const lines = text.split(/\\n|\n/g);

      return lines.map((line, j) => (
        <React.Fragment key={`${i}-${j}`}>
          {isBold ? <strong>{line}</strong> : line}
          {j < lines.length - 1 && <br />}
        </React.Fragment>
      ));
    });
  };

  return (
    <div
      id="prescription"
      className="prescription-container bg-white p-4 max-w-[450px]"
    >
      <header className="prescription-header flex flex-row">
        <img
          src={hospData?.logo}
          alt="Hospital Logo"
          className="hospital-logo w-24 rounded-md object-contain mr-4"
        />
        <div className="hospital-info w-[70%] flex flex-col h-max">
          <div className="h-[50%] flex justify-center items-center">
            <h1 className="text-xl flex font-semibold items-center text-center justify-center">
              {hospData?.hospital_name}
            </h1>
          </div>
          <div className="h-[50%] flex flex-row gap-6 justify-between mt-2">
            <div className="w-[50%]">
              <h1 className="text-[8px] font-bold">
                Dr. {docDetails?.full_name} ({docDetails?.specialization})
              </h1>
              <h1 className="text-[8px] font-bold">
                {docDetails?.qualification}
              </h1>
              <p>
                <p className="text-[8px] text-black">
                  <span className="text-[8px] font-bold ">Address: </span>
                  {hospData?.hospital_address}
                </p>
              </p>
            </div>
            <div className="w-[50%] flex flex-col items-start-end ">
              {hospData?.timing && (
                <h1 className="text-[8px]">
                  <span className="text-[8px] font-bold ">Timings: </span>
                  {hospData?.timing}
                </h1>
              )}
              <p className="text-[8px]">
                <span className="text-[8px] font-bold ">Contact:</span> +
                {hospData?.hospital_contact_number}
              </p>
              <h1 className="text-[8px]">
                <span className="text-[8px] font-bold">Date: </span>
                {moment(type === "create" ? moment() : booking?.booked_date)
                  .tz(timezone || "Asia/Kolkata")
                  .format("DD MMM, YYYY")}
              </h1>
            </div>
          </div>
        </div>
      </header>

      <section className="doctor-section mt-4 section-break">
        <div className="flex flex-row justify-between ">
          <h1 className="text-[10px] font-semibold">
            {type === "create"
              ? "Patient Name, Genger (Age)"
              : `${booking?.patient_full_name || patientName}${
                  gender ? ", " + gender : ""
                } ${
                  age && Number(age) > 0 && Number(age) < 120
                    ? `(${Number(age)} yrs)`
                    : ""
                }`}
          </h1>
          <h1 className="text-[10px] font-semibold">
            {type === "create"
              ? "Patient Number"
              : patientContactNumber
              ? "+" + patientContactNumber
              : booking?.patient_contact_number
              ? "+" + booking?.patient_contact_number
              : ""}
          </h1>
        </div>
        {vitals &&
          (vitals.bp ||
            vitals.weight ||
            vitals.pulse ||
            vitals.temperature) && (
            <div className="grid grid-cols-4 gap-x-2 mt-1 text-[9px]">
              {vitals.bp && (
                <div className="font-medium">
                  <strong>BP:</strong> {sanitizeInput(vitals.bp)}
                </div>
              )}
              {vitals.weight && (
                <div className="font-medium">
                  <strong>Weight:</strong> {sanitizeInput(vitals.weight)} kg
                </div>
              )}
              {vitals.pulse && (
                <div className="font-medium">
                  <strong>Pulse:</strong> {sanitizeInput(vitals.pulse)}
                </div>
              )}
              {vitals.temperature && (
                <div className="font-medium">
                  <strong>Temp:</strong> {sanitizeInput(vitals.temperature)}
                </div>
              )}
            </div>
          )}
        <div className="h-[1px] bg-black my-2"></div>

        {complaints && complaints.trim() !== "" && (
          <>
            <div className="flex flex-row gap-2 section-break">
              <h2 className="w-20 text-[#335FE4] text-[10px] font-semibold">
                Chief complaints
              </h2>
              <h2 className="w-80 items-start text-[10px] text-justify">
                {sanitizeInput(complaints)}
              </h2>
            </div>
            <div className="h-[1px] bg-gray-300 my-2"></div>
          </>
        )}

        {historyAndExaminations && historyAndExaminations.trim() !== "" && (
          <>
            <div className="flex flex-row gap-2 section-break">
              <h2 className="w-20 text-[#335FE4] text-[10px] font-semibold">
                History & Examinations
              </h2>
              <h2 className="w-80 items-start text-[10px] text-justify">
                {sanitizeInput(historyAndExaminations)}
              </h2>
            </div>
            <div className="h-[1px] bg-gray-300 my-2"></div>
          </>
        )}

        {/* ✅ Examination Findings Table */}
        {examinations && examinations.length > 0 && (
          <>
            <div className="flex flex-col gap-2 section-break mt-2">
              <h2 className="w-full text-black text-[10px] font-semibold">
                Examination Findings
              </h2>
              <div className="w-full">
                <div className="overflow-hidden rounded-sm border border-gray-200 mt-1">
                  <table className="min-w-full divide-y divide-gray-200 table-auto">
                    <thead className="bg-blue-700 border-[#335FE4]">
                      <tr className="!bg-[#335FE4]">
                        <th className="px-3 py-2 text-left text-[10px] font-medium text-black uppercase tracking-wider">
                          Finding
                        </th>
                        <th className="px-3 py-2 text-left text-[10px] font-medium text-black uppercase tracking-wider">
                          Result
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {examinations.map((exam, index) => (
                        <tr
                          key={index}
                          className={
                            index % 2 === 0 ? "bg-white" : "bg-gray-50"
                          }
                        >
                          <td className="px-3 py-3 text-[10px] text-justify text-gray-700">
                            {sanitizeInput(exam.finding)}
                          </td>
                          <td className="px-3 py-3 text-[10px] text-justify text-gray-700">
                            {sanitizeInput(exam.result)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            <div className="h-[1px] bg-gray-300 my-2"></div>
          </>
        )}

        {investigations && investigations.trim() !== "" && (
          <>
            <div className="flex flex-row gap-2 section-break">
              <h2 className="w-20 text-[#335FE4] text-[10px] font-semibold">
                Investigations
              </h2>
              <h2 className="w-80 items-start text-[10px] text-justify">
                {sanitizeInput(investigations)}
              </h2>
            </div>
            <div className="h-[1px] bg-gray-300 my-2"></div>
          </>
        )}

        {diagnosis && diagnosis.trim() !== "" && (
          <>
            <div className="flex flex-row gap-2 section-break">
              <h2 className="w-20 text-[#335FE4] text-[10px] font-semibold">
                Diagnosis
              </h2>
              <h2 className="w-80 items-start text-[10px] text-justify">
                {sanitizeInput(diagnosis)}
              </h2>
            </div>
            <div className="h-[1px] bg-gray-300 my-2"></div>
          </>
        )}

        {previousMedicines && previousMedicines.trim() !== "" && (
          <>
            <div className="flex flex-row gap-2 section-break">
              <h2 className="w-20 text-[#335FE4] text-[10px] font-semibold">
                Previous Medicines
              </h2>
              <h2 className="w-80 items-start text-[10px] text-justify">
                {sanitizeInput(previousMedicines)}
              </h2>
            </div>
            <div className="h-[1px] bg-gray-300 my-2"></div>
          </>
        )}
      </section>

      <section
        className="medicines-section mt-4 section-break"
        id="medicines-section"
      >
        <h3 className="mb-2 font-bold text-gray-700 text-[10px] ">
          Prescription
        </h3>
        <div className="overflow-hidden rounded-sm border border-gray-200 mt-4">
          <table className="min-w-full divide-y divide-gray-200 table-auto">
            <thead className="bg-blue-700 border-[#335FE4]">
              <tr className="!bg-[#335FE4]">
                <th className="px-3 py-2 text-left text-[10px] font-medium text-black uppercase tracking-wider">
                  Medicine
                </th>
                <th className="px-3 py-2 text-left text-[10px] font-medium text-black uppercase tracking-wider">
                  Dosage
                </th>
                <th className="px-3 py-2 text-left text-[10px] font-medium text-black uppercase tracking-wider">
                  Duration
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {medicines.length > 0 ? (
                medicines.map((data, index) => (
                  <tr
                    key={index}
                    className={index % 2 === 0 ? "bg-white" : "bg-gray-50"}
                  >
                    <td className="px-3 py-3 text-[10px] text-gray-700">
                      {sanitizeInput(data.name)}
                    </td>
                    <td className="px-3 py-3 text-[10px] text-gray-700">
                      {sanitizeInput(data.dosage)}
                    </td>
                    <td className="px-3 py-3 text-[10px] text-gray-700">
                      {sanitizeInput(data.duration)}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={3}
                    className="px-3 py-3 text-center text-xs text-gray-500"
                  >
                    No medicines prescribed.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section className="advice-section section-break">
        {advice && advice.trim() !== "" && (
          <>
            <div className="h-[1px] bg-gray-300 mt-8 mb-4"></div>
            <div className="flex flex-row gap-2">
              <h2 className="w-20 text-[#335FE4] text-[10px] font-bold">
                Advice
              </h2>
              <p className="text-[10px] w-80 text-justify">
                {renderFormattedText(advice)}
              </p>
            </div>

            <div className="h-[1px] bg-gray-300 my-4"></div>
          </>
        )}
        <div className="flex w-full justify-end">
          <section className="footer-section section-break flex flex-col justify-items-end w-[30%] ">
            <h2 className="text-[8px] mt-4 text-right">
              Electronically Signed by:
            </h2>
            <div className="flex flex-col items-start-end gap-2">
              {signatureImageUrl && (
                <div className="text-center">
                  <img
                    src={signatureImageUrl}
                    alt="Uploaded Signature"
                    className="mt-2 w-[90%] object-contain"
                  />
                </div>
              )}
              <p className="text-right text-[9px] font-bold whitespace-nowrap">
                Dr. {docDetails?.full_name}
              </p>
              <p className="text-right text-[8px] whitespace-nowrap">
                {docDetails?.specialization}
              </p>
            </div>
          </section>
        </div>
      </section>
    </div>
  );
};

export default PrescriptionPdf;
